"use client";

import { cn } from "@/core/lib/utils";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/core/components/ui/accordion";
import { Button } from "@/core/components/ui/button";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AGENT_THEMES, WelcomeAgentIcon } from "../constants/agents";
import type { AgentShortcut, AgentType } from "../types";

type AgentHubPickerProps = {
  defaultAgent?: AgentType;
  onShortcutClick: (agentType: AgentType, shortcut: AgentShortcut) => void;
};

// Experiência incluída: dono da action "motivos_de_uso_insights" (PED-9109).
const AGENT_ORDER: AgentType[] = ["operation", "estrategy", "experience"];
type HeaderStage = "hub" | AgentType;
const HEADER_ROTATION: HeaderStage[] = ["hub", ...AGENT_ORDER];
const HEADER_ROTATION_INTERVAL_MS = 3500;

export function AgentHubPicker({
  defaultAgent = "operation",
  onShortcutClick,
}: AgentHubPickerProps) {
  const [translate] = useTranslation(["core"]);
  const [openItem, setOpenItem] = useState<string>(defaultAgent);
  const [headerStageIndex, setHeaderStageIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setHeaderStageIndex((i) => (i + 1) % HEADER_ROTATION.length);
    }, HEADER_ROTATION_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  const headerStage = HEADER_ROTATION[headerStageIndex];
  const activeTheme =
    headerStage === "hub" ? undefined : AGENT_THEMES[headerStage];

  const headerKey = headerStage;
  const headerIcon = activeTheme?.iconSrc ?? WelcomeAgentIcon;
  const headerNameKey = activeTheme?.nameKey ?? "chatbot-hub-ia-name";
  const headerDescription = activeTheme
    ? translate(activeTheme.descriptionKey)
    : translate("chatbot-hub-intro");

  return (
    <div className="flex flex-col items-center px-4">
      <div
        key={headerKey}
        className="flex flex-col items-center w-full animate-in fade-in slide-in-from-bottom-2 duration-300"
      >
        <img
          src={headerIcon}
          alt=""
          aria-hidden
          className="mb-3 w-24 h-24 transition-all duration-300"
        />

        <h2 className="text-[24px] text-darkgray-900 mb-2 font-bold">
          {translate("chatbot-hub-title-prefix")}{" "}
          <i className="font-serif italic">
            {translate(headerNameKey) as string}
          </i>
        </h2>

        <p className="px-2 mb-6 text-[16px] text-center text-darkgray-600">
          {headerDescription}
        </p>
      </div>

      <Accordion
        type="single"
        collapsible
        value={openItem}
        onValueChange={(v) => setOpenItem(v)}
        className="flex flex-col gap-0 w-full rounded-3xl border divide-y"
      >
        {AGENT_ORDER.map((agentType) => {
          const theme = AGENT_THEMES[agentType];

          return (
            <AccordionItem
              key={agentType}
              value={agentType}
              className="rounded-none border-0 shadow-none py-[6px]"
            >
              <AccordionTrigger className="px-4 hover:no-underline">
                <div className="flex gap-3 items-center">
                  <img
                    src={theme.iconSrc}
                    alt=""
                    aria-hidden
                    className="object-contain w-6 h-6"
                  />
                  <span className="text-[16px] font-semibold text-darkgray-700">
                    {translate("chatbot-hub-title-prefix")}{" "}
                    <i className={cn("font-serif italic text-[17px]")}>
                      {translate(theme.nameKey) as string}
                    </i>
                  </span>
                </div>
              </AccordionTrigger>

              <AccordionContent className="px-4">
                <p className="mb-[24px] text-[14px] text-darkgray-500">
                  {translate(theme.descriptionKey)}
                </p>

                {theme.shortcuts.length > 0 && (
                  <div className="flex flex-col gap-2">
                    {theme.shortcuts.map((shortcut) => (
                      <Button
                        key={shortcut.action}
                        variant="outline"
                        className={cn(
                          "justify-start w-full h-auto px-4 py-3 rounded-2xl",
                          "text-[16px] font-semibold text-left whitespace-normal",
                          "bg-white border-lightgray-300 text-darkgray-500",
                          "hover:bg-lightgray-200",
                        )}
                        onClick={() => onShortcutClick(agentType, shortcut)}
                      >
                        <span className="flex flex-wrap gap-2 items-center">
                          {translate(shortcut.textKey)}
                          {shortcut.badgeKey && (
                            <span className="rounded-full bg-primary-500 px-2 py-0.5 text-[12px] font-semibold text-darkgray-800">
                              {translate(shortcut.badgeKey)}
                            </span>
                          )}
                        </span>
                      </Button>
                    ))}
                  </div>
                )}
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </div>
  );
}
